/* variant-savings.js
   Global pack sorting + savings display for Dawn variant radios/selects.
   - BEST PRICE = lowest discounted price
   - MOST VALUE = highest savings amount
   - Shows: price + Save ₹ + % off (no original price shown inside cards)
*/

(function () {
  function moneyFromCents(cents, currency) {
    // Simple INR-friendly formatting; uses Intl if available
    try {
      const value = cents / 100;
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: currency || "INR",
        maximumFractionDigits: 0,
      }).format(value);
    } catch (e) {
      // fallback
      return "₹" + Math.round(cents / 100).toLocaleString("en-IN");
    }
  }

  function getCurrency() {
    // Shopify usually exposes this
    if (window.Shopify && Shopify.currency && Shopify.currency.active) return Shopify.currency.active;
    return "INR";
  }

  function parseVariantsJSON(root) {
    const jsonEl = root.querySelector('script[type="application/json"]');
    if (!jsonEl) return null;
    try {
      return JSON.parse(jsonEl.textContent.trim());
    } catch (e) {
      return null;
    }
  }

  function isPackLikeLegend(text) {
    const t = (text || "").toLowerCase();
    return t.includes("pack") || t.includes("combo") || t.includes("bundle") || t.includes("quantity") || t.includes("set");
  }

  function shouldEnhance(fieldset, variants, productHasSingleOption) {
    // Only enhance if:
    // 1) looks like pack/combo option OR
    // 2) product has a single option (common for pack size products)
    const legend = fieldset.querySelector("legend");
    const legendText = legend ? legend.textContent : "";
    if (productHasSingleOption) return true;
    return isPackLikeLegend(legendText);
  }

  function buildVariantMapForSingleOption(variants) {
    // For single-option products, each variant title equals option value
    const map = new Map();
    variants.forEach(v => map.set(v.title, v));
    return map;
  }

  function clearBadgesAndMeta(label) {
    label.querySelectorAll(".kv-badge, .kv-meta").forEach(el => el.remove());
    label.classList.remove("is-best-price", "is-most-value");
    label.removeAttribute("data-kv-price");
    label.removeAttribute("data-kv-savings");
  }

  function enhanceFieldset(fieldset, variants, currency, productHasSingleOption) {
    const inputs = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
    if (!inputs.length) return;

    const optionValueToVariant = productHasSingleOption ? buildVariantMapForSingleOption(variants) : null;

    // Build list of {input,label,variant,price,savings,percent}
    const items = [];
    inputs.forEach((input) => {
      const id = input.getAttribute("id");
      const label = id ? fieldset.querySelector(`label[for="${CSS.escape(id)}"]`) : null;
      if (!label) return;

      clearBadgesAndMeta(label);

      const optionValue = input.value;
      let variant = null;

      if (productHasSingleOption && optionValueToVariant) {
        variant = optionValueToVariant.get(optionValue) || null;
      } else {
        // Multi-option: best effort match by variant option1 when legend is pack-like.
        // (This keeps it safer for color/size swatches.)
        variant = variants.find(v => v.option1 === optionValue) || null;
      }

      if (!variant) return;

      const price = Number(variant.price || 0);
      const compareAt = Number(variant.compare_at_price || 0);

      const savings = compareAt > price ? (compareAt - price) : 0;
      const percent = (compareAt > price && compareAt > 0) ? Math.round((savings / compareAt) * 100) : 0;

      // Store values for sorting + CSS
      label.setAttribute("data-kv-price", String(price));
      label.setAttribute("data-kv-savings", String(savings));

      


      items.push({ input, label, variant, price, savings, percent });
    });

    if (items.length < 2) return;

    // Sort order for display: low price -> higher price (best price first)
    // But we still mark MOST VALUE separately.
    const sortedByPrice = [...items].sort((a, b) => a.price - b.price);

    // Most value = max savings (tie-breaker: higher percent, then higher price)
    const mostValue = [...items].sort((a, b) => {
      if (b.savings !== a.savings) return b.savings - a.savings;
      if (b.percent !== a.percent) return b.percent - a.percent;
      return b.price - a.price;
    })[0];

    const bestPrice = sortedByPrice[0];

    // Reorder DOM (label+input order must remain as a pair in Dawn)
    // Dawn structure: input + label siblings. We'll move BOTH together.
    const parent = items[0].input.parentElement; // usually fieldset
    if (!parent) return;

    const pairs = sortedByPrice.map(it => ({ input: it.input, label: it.label }));

    // Move in order
    pairs.forEach(pair => {
      parent.appendChild(pair.input);
      parent.appendChild(pair.label);
    });

    // Add badges
    if (bestPrice && bestPrice.label) {
      bestPrice.label.classList.add("is-best-price");
      const badge = document.createElement("span");
      badge.className = "kv-badge kv-badge--best";
      badge.textContent = "BEST PRICE";
      bestPrice.label.appendChild(badge);
    }

    if (mostValue && mostValue.label && mostValue.label !== bestPrice.label) {
      mostValue.label.classList.add("is-most-value");
      const badge = document.createElement("span");
      badge.className = "kv-badge kv-badge--value";
      badge.textContent = "MOST VALUE";
      mostValue.label.appendChild(badge);
    }
  }

  function init(root) {
    const variants = parseVariantsJSON(root);
    if (!variants || !Array.isArray(variants)) return;

    const currency = getCurrency();
    const productHasSingleOption = (root.querySelectorAll("fieldset").length === 1);

    const fieldsets = Array.from(root.querySelectorAll("fieldset.js.product-form__input"));
    fieldsets.forEach(fs => {
      if (!shouldEnhance(fs, variants, productHasSingleOption)) return;
      enhanceFieldset(fs, variants, currency, productHasSingleOption);
    });
  }

  function boot() {
    const roots = Array.from(document.querySelectorAll("variant-radios, variant-selects"));
    roots.forEach(init);

    // Re-run when variant UI updates (Dawn updates DOM sometimes)
    document.addEventListener("variant:change", () => {
      const roots2 = Array.from(document.querySelectorAll("variant-radios, variant-selects"));
      roots2.forEach(init);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
